import type { SubmitEventHandler } from 'react';

import { useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { getTestProps } from '../../lib/helpers';

import Button from '../../components/Button/Button';

import { PlusCircleOutlineIcon } from '../../images/shapes';

import { useAppForm } from '../../form-hooks/form';

interface CustomUIPluginFormProps {
  /**
   * On submit callback
   */
  onSubmit: (url: string) => Promise<boolean>;
  /**
   * If form is disabled
   */
  disabled?: boolean;
  /**
   * Form test id
   */
  testId?: string;
}

const initialValues = {
  url: '',
};

const CustomUIPluginForm = ({
  onSubmit,
  disabled = false,
  testId = '',
}: CustomUIPluginFormProps) => {
  const { t } = useTranslation();

  const form = useAppForm({
    defaultValues: initialValues,
    onSubmit: async ({ value, formApi }) => {
      const resetForm = await onSubmit(value.url);
      if (resetForm) {
        formApi.reset();
      }
    },
  });

  const handleSubmit = useCallback<SubmitEventHandler>(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      form.handleSubmit();
    },
    [form],
  );

  return (
    <form.AppForm>
      <form
        id={'custom-ui-plugin-form'}
        className="pb-3"
        onSubmit={handleSubmit}
        noValidate={true}
      >
        <div className="flex items-end gap-2">
          <form.AppField name="url">
            {(field) => (
              <field.Input
                label={t('Plugins.CustomPlugin')}
                placeholder={t('Plugins.CustomPluginPlaceholder')}
                additionalClasses={'mt-2'}
                {...getTestProps(testId, 'url', 'testId')}
              />
            )}
          </form.AppField>

          <Button
            type="submit"
            form="custom-ui-plugin-form"
            buttonColor="borderlessBlue"
            disabled={disabled}
            additionalClasses="mb-2"
            {...getTestProps(testId, 'add-custom-plugin', 'testId')}
          >
            <PlusCircleOutlineIcon className="min-w-5 w-5" />
          </Button>
        </div>
        <div className="text-sm text-slate-600 dark:text-slate-200 mt-1">
          <Trans i18nKey="Plugins.AllowedHostsTooltip">
            {{ hosts: 'localhost' }}
          </Trans>
        </div>
      </form>
    </form.AppForm>
  );
};

export default CustomUIPluginForm;
